import { z } from 'zod';

import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import { CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH } from './cash-error-codes.js';
import { CashOperationIdentityDtoSchema } from './cash-operation-identity.schema.js';
import {
  cashLocalTransferKindValues,
  cashLocalTransferStatusValues,
  type CashLocalTransferDto,
  type CashLocalTransferCorrectionDto,
  type CashLocalTransferKind,
  type CashLocalTransferStatus,
  type ListCashLocalTransfersRequest,
  type ListCashLocalTransfersResponse,
  type PerformCashLocalTransferRequest,
  type RecordCashLocalTransferCorrectionRequest,
  type ListCashLocalTransferCorrectionsRequest,
  type ListCashLocalTransferCorrectionsResponse,
  type ReplaceCashLocalTransferRequest,
  type ReverseCashLocalTransferRequest,
} from './cash-local-transfers.dto.js';

const timestampSchema = z.iso.datetime();

export const CashLocalTransferKindSchema: z.ZodType<CashLocalTransferKind> = z.enum(
  cashLocalTransferKindValues,
);
export const CashLocalTransferStatusSchema: z.ZodType<CashLocalTransferStatus> = z.enum(
  cashLocalTransferStatusValues,
);

export const CashLocalTransferDtoSchema: z.ZodType<CashLocalTransferDto> =
  CashOperationIdentityDtoSchema.extend({
    id: z.uuid(),
    kind: CashLocalTransferKindSchema,
    status: CashLocalTransferStatusSchema,
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    sourceCashLocationId: z.uuid(),
    sourceCashSessionId: z.uuid().nullable(),
    destinationCashLocationId: z.uuid(),
    destinationCashSessionId: z.uuid().nullable(),
    amountMinor: z.number().int().positive(),
    note: z.string().trim().min(1).max(500).nullable(),
    denominations: CashDenominationRowsSchema.nullable(),
    sourceMovementId: z.uuid(),
    destinationMovementId: z.uuid(),
    cashNeededRequestId: z.uuid().nullable(),
    reversedByCashTransferId: z.uuid().nullable(),
    reversesCashTransferId: z.uuid().nullable(),
    replacesCashTransferId: z.uuid().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const CashLocalTransferCorrectionDtoSchema: z.ZodType<CashLocalTransferCorrectionDto> =
  CashOperationIdentityDtoSchema.extend({
    id: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    originalCashTransferId: z.uuid(),
    actualAmountMinor: z.number().int().nonnegative(),
    reason: z.string().trim().min(1).max(500),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const ListCashLocalTransfersRequestSchema: z.ZodType<ListCashLocalTransfersRequest> =
  z.object({
    companyId: z.uuid(),
    locationId: z.uuid().optional(),
    kind: CashLocalTransferKindSchema.optional(),
    status: CashLocalTransferStatusSchema.optional(),
  });

export const ListCashLocalTransfersResponseSchema: z.ZodType<ListCashLocalTransfersResponse> =
  z.object({ items: z.array(CashLocalTransferDtoSchema) });

export const PerformCashLocalTransferRequestSchema: z.ZodType<PerformCashLocalTransferRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    kind: CashLocalTransferKindSchema,
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    sourceCashLocationId: z.uuid(),
    sourceCashSessionId: z.uuid().nullable().optional(),
    destinationCashLocationId: z.uuid(),
    destinationCashSessionId: z.uuid().nullable().optional(),
    amountMinor: z.number().int().positive(),
    note: z.string().trim().max(500).nullable().optional(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    cashNeededRequestId: z.uuid().nullable().optional(),
    occurredAt: timestampSchema.optional(),
  })
  .superRefine((value, context) => {
    if (value.sourceCashLocationId === value.destinationCashLocationId) {
      context.addIssue({
        code: 'custom',
        path: ['destinationCashLocationId'],
        message: 'Source and destination Cash Locations must differ.',
      });
    }
    if (value.kind === 'drawer_refill' && value.sourceCashSessionId != null) {
      context.addIssue({
        code: 'custom',
        path: ['sourceCashSessionId'],
        message: 'Vault source cannot have a Cash session.',
      });
    }
    if (value.kind === 'drawer_drop' && value.destinationCashSessionId != null) {
      context.addIssue({
        code: 'custom',
        path: ['destinationCashSessionId'],
        message: 'Vault destination cannot have a Cash session.',
      });
    }
    if (value.kind === 'drawer_to_drawer') {
      if (!value.sourceCashSessionId) {
        context.addIssue({
          code: 'custom',
          path: ['sourceCashSessionId'],
          message: 'Drawer-to-Drawer source Cash session is required.',
        });
      }
      if (!value.destinationCashSessionId) {
        context.addIssue({
          code: 'custom',
          path: ['destinationCashSessionId'],
          message: 'Drawer-to-Drawer destination Cash session is required.',
        });
      }
    }
    if (value.denominations) {
      const denominationTotalMinor = value.denominations.reduce(
        (total, row) => total + row.denom * row.count,
        0,
      );
      if (denominationTotalMinor !== value.amountMinor) {
        context.addIssue({
          code: 'custom',
          path: ['denominations'],
          message: 'Denomination total does not match the local Cash transfer amount.',
          params: { apiErrorCode: CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH },
        });
      }
    }
  });

export const ReverseCashLocalTransferRequestSchema: z.ZodType<ReverseCashLocalTransferRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    cashTransferId: z.uuid(),
    reason: z.string().trim().min(1).max(500),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    occurredAt: timestampSchema.optional(),
  });

export const ReplaceCashLocalTransferRequestSchema: z.ZodType<ReplaceCashLocalTransferRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    cashTransferId: z.uuid(),
    replacementAmountMinor: z.number().int().positive(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    occurredAt: timestampSchema.optional(),
  });

export const RecordCashLocalTransferCorrectionRequestSchema: z.ZodType<RecordCashLocalTransferCorrectionRequest> =
  z.object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    originalCashTransferId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    actualAmountMinor: z.number().int().nonnegative(),
    reason: z.string().trim().min(1).max(500),
    occurredAt: timestampSchema.optional(),
  });

export const ListCashLocalTransferCorrectionsRequestSchema: z.ZodType<ListCashLocalTransferCorrectionsRequest> =
  z.object({
    companyId: z.uuid(),
    locationId: z.uuid().optional(),
    originalCashTransferId: z.uuid().optional(),
  });

export const ListCashLocalTransferCorrectionsResponseSchema: z.ZodType<ListCashLocalTransferCorrectionsResponse> =
  z.object({ items: z.array(CashLocalTransferCorrectionDtoSchema) });
